import { AsyncPipe, DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, OnInit } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import {
    BehaviorSubject,
    filter,
    iif,
    map,
    merge,
    Observable,
    scan,
    shareReplay,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { ButtonComponent } from "~app/common/components/button/button.component";
import { BlockComponent } from "~app/common/components/container-block/block.component";
import { IconButtonComponent } from "~app/common/components/icon-button/icon-button.component";
import { ListColumnDirective } from "~app/common/components/list/list-column.directive";
import { ListComponent } from "~app/common/components/list/list.component";
import { PageComponent } from "~app/common/components/page/page.component";
import { AnvandareVikarieDto } from "./models/anvandareVikarieDto";
import { CreateAnvandareVikarieCommand } from "./models/createAnvandareVikarieCommand";
import { GetAnvandareVikarieQuery } from "./models/getAnvandareVikarieQuery";
import { UpdateAnvandareVikarieCommand } from "./models/updateAnvandareVikarieCommand";
import { VikariehanteringModalComponent } from "./vikariehantering-modal/vikariehantering-modal.component";
import { VikariehanteringService } from "./vikariehantering.service";

type VirkeskopareReducer = (curr: AnvandareVikarieDto[]) => AnvandareVikarieDto[];

@Component({
    selector: "rask-vikariehantering",
    imports: [
        ListComponent,
        ListColumnDirective,
        AsyncPipe,
        ButtonComponent,
        ReactiveFormsModule,
        RouterLink,
        IconButtonComponent,
        DatePipe,
        PageComponent,
        BlockComponent
    ],
    templateUrl: "./vikariehantering.component.html",
    styleUrl: "./vikariehantering.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VikariehanteringComponent implements OnInit {
    protected anvandareVikarier$: Observable<AnvandareVikarieDto[]>;
    protected readonly createClickSrc = new Subject<void>();
    protected readonly removeClickSrc = new Subject<AnvandareVikarieDto>();
    private readonly route = inject(ActivatedRoute);
    private readonly modal = inject(NgbModal);
    private readonly router = inject(Router);
    private readonly vikariehanteringService = inject(VikariehanteringService);
    private readonly reloadSrc = new BehaviorSubject<string>("");
    private readonly updateSrc = new Subject<AnvandareVikarieDto>();

    private readonly reloadReducer: Observable<VirkeskopareReducer> = this.reloadSrc.pipe(
        switchMap(() => this.getAnvandareVikarier(null)),
        map((result) => (_curr) => result),
        shareReplay(1)
    );

    private readonly updateReducer: Observable<VirkeskopareReducer> = this.updateSrc.pipe(
        switchMap(v => iif(() => v.id > 0, this.updateAnvandareVikarie(v), this.createAnvandareVikarie(v))),
        map((v) => (curr) => {
            const index = curr.findIndex(x => x.id === v.id);
            if (index > -1) {
                return [
                    ...curr.slice(0, index),
                    v,
                    ...curr.slice(index + 1)
                ];
            }
            return [
                ...curr,
                v
            ];
        })
    );

    private readonly createReducer: Observable<VirkeskopareReducer> = this.createClickSrc.pipe(
        tap(() => this.openModal()),
        map(() => (curr) => curr)
    );

    private readonly removeReducer: Observable<VirkeskopareReducer> = this.removeClickSrc.pipe(
        switchMap(deleted => this.deleteAnvandareVikarie(deleted)),
        map(removed => (curr) => curr.filter(x => x.id !== removed.id))
    );

    private readonly editReducer: Observable<VirkeskopareReducer> = this.route.queryParams.pipe(
        map(params => +params.id),
        filter(id => id > 0),
        switchMap(id => this.getAnvandareVikarier({
            id: id
        })),
        tap(res => this.openModal(res[0])),
        map(() => (curr) => curr)
    );

    ngOnInit(): void {
        this.anvandareVikarier$ = merge(
            this.editReducer,
            this.updateReducer,
            this.reloadReducer,
            this.createReducer,
            this.removeReducer
        ).pipe(
            scan((acc: AnvandareVikarieDto[], reducer: VirkeskopareReducer) => reducer(acc), [])
        );
    }

    private async openModal(dto?: AnvandareVikarieDto): Promise<void> {
        const modalRef = this.modal.open(VikariehanteringModalComponent, {
            backdrop: "static",
            size: "md"
        });

        const instance = modalRef.componentInstance as VikariehanteringModalComponent;
        instance.virkeskopare = dto;

        try {
            const result: AnvandareVikarieDto = await modalRef.result;
            if (result) {
                this.updateSrc.next(result);
            }
        } finally {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {}
            });
        }
    }

    private getAnvandareVikarier(query: GetAnvandareVikarieQuery): Observable<AnvandareVikarieDto[]> {
        return this.vikariehanteringService.getVirkeskopare(query);
    }

    private createAnvandareVikarie(dto: AnvandareVikarieDto): Observable<AnvandareVikarieDto> {
        const command = new CreateAnvandareVikarieCommand(instance => {
            instance.anvandareId = dto.anvandare.id;
            instance.vikarieId = dto.vikarie.id;
            instance.franDatum = dto.franDatum;
            instance.tillDatum = dto.tillDatum;
        });

        return this.vikariehanteringService.createAnvandareVikarie(command);
    }

    private updateAnvandareVikarie(dto: AnvandareVikarieDto): Observable<AnvandareVikarieDto> {
        const command = new UpdateAnvandareVikarieCommand(instance => {
            instance.id = dto.id;
            instance.vikarieId = dto.vikarie.id;
            instance.franDatum = dto.franDatum;
            instance.tillDatum = dto.tillDatum;
        });

        return this.vikariehanteringService.updateAnvandareVikarie(command);
    }

    private deleteAnvandareVikarie(dto: AnvandareVikarieDto): Observable<AnvandareVikarieDto> {
        return this.vikariehanteringService.deleteAnvandareVikarie(dto.id).pipe(
            map(() => dto)
        );
    }
}
